import React, { useState } from 'react';

import { Icon } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton, {
  ActionButtonSizes,
  ActionButtonVariants,
  ActionLink,
} from 'components/action-button';
import { BackArrowLink } from 'components/navigation/back-arrow';
import Picture from 'components/picture';
import QRCode from 'components/qrcode';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { useFeaturePayback } from 'hooks/use-feature-payback';
import AuthRequired from 'pages/authentication/auth-required';
import { routes } from 'utils/routing';

// Declare the payback custom component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'pb-login': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
  interface Window {
    paybackde: {
      loginWithPaybackResultManager: {
        addListener: (callbacks: {
          onLoginSuccess: () => void;
          onLoginFail: () => void;
          onLoginCancel: () => void;
        }) => void;
      };
    };
  }
}

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-flow: column;

  ${Styles.desktop`
    padding: 8rem 0 1rem 0;
  `}
`;

const MainImage = styled(Picture)`
  width: 60%;
  margin: 10vh auto 1em;

  ${Styles.desktop`
    width: 400px;
  `}
`;

const PaybackLogo = styled(Picture)`
  width: 10rem;
  margin: 10vh auto 1em;
`;

const Title = styled.h2`
  font-weight: 700;
  text-align: center;
  margin-bottom: 5px;
`;

const Description = styled(SanityBlockRenderer)`
  margin: 0 auto;
  padding: 0 10px;
  text-align: center;
  font-size: 1rem;
`;

const PointsContainer = styled.div`
  margin: 1rem;
  text-align: center;
`;

const Points = styled.div`
  font-family: ${Styles.fontFamily.brand};
  font-size: 1.4rem;
`;

const QRContainer = styled.div`
  margin: 2rem auto;
  width: 180px;
  height: 180px;
  background-color: white;
`;

const LinkButton = styled(ActionButton)`
  margin: 3em auto 2em;
`;

const NeedHelpButton = styled(ActionLink)`
  margin: 1em auto 0;
  color: ${Styles.color.primary};
  text-decoration: underline;
  font-weight: 300;
  font-size: 1.125rem;
`;

const RefreshButton = styled(ActionButton)`
  margin: 0 auto 3rem;
`;

// TODO: Replace logic here when backend is ready
const hasPaybackToken = false;

const PaybackVendor = () => {
  const { featurePaybackLoading, featurePayback } = useFeaturePayback();
  const { formatMessage } = useIntl();
  const [displayIframe, setDisplayIframe] = useState(false);

  const authenticatePayback = () => {
    if (!featurePayback?.paybackScriptSrc) {
      return;
    }

    // Attaching Payback's script that loads the iframe replacing the pb-login component
    const script = document.createElement('script');
    script.src = featurePayback?.paybackScriptSrc;
    script.async = true;
    script.dataset.testid = 'payback-bootstrap';
    document.body.appendChild(script);

    // Hooks for Payback's iframe events
    script.addEventListener('load', () => {
      window.paybackde?.loginWithPaybackResultManager?.addListener({
        onLoginSuccess: () => {
          // TODO : onLoginSuccess flow
        },
        onLoginFail: () => {
          // TODO : onLoginFail flow
        },
        onLoginCancel: () => {
          // TODO : onLoginCancel flow
        },
      });
    });

    // Hide our elements while the iframe is visible
    setDisplayIframe(true);
  };

  return featurePaybackLoading ? null : (
    <AuthRequired>
      <Container>
        <BackArrowLink to={routes.account} />

        {hasPaybackToken ? (
          <>
            <PaybackLogo image={featurePayback?.paybackLogoImage?.locale} alt="" objectFitContain />
            <PointsContainer>
              {formatMessage({ id: 'rutrYouHave' })}
              <Points>{formatMessage({ id: 'pointsNumber' }, { points: 90 })}</Points>
              <QRContainer>
                <QRCode
                  barcode={`TESTCODE|SR`}
                  options={{
                    width: 180,
                  }}
                  data-testid="qr-code"
                />
              </QRContainer>
            </PointsContainer>
            <RefreshButton
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              data-testid="payback-refresh-button"
              onClick={authenticatePayback}
              startIcon={<Icon icon="bkCrown" color="primary" aria-hidden />}
            >
              {featurePayback?.paybackRefreshPointsButtonText?.locale}
            </RefreshButton>
          </>
        ) : (
          <>
            <pb-login
              style={{
                // This element needs to be available in the DOM
                // for the Payback script to work so we hide it with CSS
                height: '80vh',
                display: displayIframe ? 'block' : 'none',
              }}
              data-partner-name={featurePayback?.paybackPartnerName}
              data-redirect-url={featurePayback?.paybackRedirectUrl}
              data-height-id="l"
              data-label-id="login"
              data-testid="payback-login-button"
            />

            {!displayIframe && (
              <>
                <MainImage
                  image={featurePayback?.paybackMainImage?.locale}
                  alt=""
                  objectFitContain
                />
                <Title>{featurePayback?.paybackTitle?.locale}</Title>
                <Description content={featurePayback?.paybackDescription?.locale} />

                <LinkButton
                  variant={ActionButtonVariants.PRIMARY}
                  aria-label={featurePayback?.paybackLinkCardButtonText?.locale || ''}
                  data-testid="link-payback-button"
                  size={ActionButtonSizes.LARGE}
                  perceptible
                  onClick={authenticatePayback}
                >
                  {featurePayback?.paybackLinkCardButtonText?.locale}
                </LinkButton>
              </>
            )}
          </>
        )}

        <NeedHelpButton
          data-testid="payback-need-help-button"
          variant={ActionButtonVariants.TEXT_ONLY}
          size={ActionButtonSizes.LARGE}
          to={routes.support}
        >
          {featurePayback?.paybackNeedHelpButtonText?.locale}
        </NeedHelpButton>
      </Container>
    </AuthRequired>
  );
};

export default PaybackVendor;
